import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import BackdropPicker from "./BackdropPicker";
import { UserDrawer, ExportUsersButton, UploadForm } from "./AdminTools";
import LogicChecker from "./LogicChecker";
import {
  LayoutDashboard, Users as UsersIcon, PenLine, Ticket, Library as LibraryIcon,
  Home as HomeIcon, Play, Loader2, Plus, Check, Users2, Activity, DollarSign, Headphones,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "studio", label: "Script Studio", icon: PenLine },
  { id: "codes", label: "Team Codes", icon: Ticket },
  { id: "content", label: "Content", icon: LibraryIcon },
];

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={`admin-stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <Icon size={18} className="text-royal" />
    <div className="font-serif text-3xl font-bold text-navy mt-2">{value}</div>
    <div className="text-xs text-slate-500 font-medium">{label}</div>
  </div>
);

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loader2 className="animate-spin text-royal" />;
  return (
    <div className="grid grid-cols-2 gap-3">
      <Stat icon={Users2} label="Total athletes" value={d.total_athletes} />
      <Stat icon={Activity} label="Active this week" value={d.active_this_week} />
      <Stat icon={Headphones} label="Sessions played" value={d.sessions_played} />
      <Stat icon={DollarSign} label="Revenue (test)" value={`$${d.revenue}`} />
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data.users)), []);
  useEffect(() => { load(); }, [load]);
  const grant = async (id) => { await api.post(`/admin/users/${id}/grant`, { credits: 10 }); toast.success("Granted 10 sessions"); load(); };
  const deactivate = async (id) => { await api.post(`/admin/users/${id}/deactivate`); toast.success("Deactivated"); load(); };
  return (
    <div className="space-y-2.5" data-testid="admin-users-list">
      <div className="flex items-center justify-between mb-1"><div className="text-sm font-bold text-navy">{users.length} athletes</div><ExportUsersButton /></div>
      {open && <UserDrawer userId={open} onClose={() => setOpen(null)} onChanged={load} />}
      {users.length === 0 && <p className="text-slate-400 text-sm">No athletes yet.</p>}
      {users.map((u) => (
        <div key={u.user_id} className="rounded-2xl bg-white border border-slate-200 p-3.5" data-testid={`admin-user-${u.user_id}`}>
          <button data-testid={`admin-user-open-${u.user_id}`} onClick={() => setOpen(u.user_id)} className="w-full flex items-center justify-between text-left">
            <div className="min-w-0">
              <div className="font-bold text-navy truncate">{u.name}</div>
              <div className="text-xs text-slate-500 capitalize">{u.sport} · {u.position || "—"} · {u.credits} credits</div>
            </div>
            <div className="text-right text-xs text-slate-500">
              <div>🔥 {u.streak}d · {u.stories_completed} done</div>
              <div>{Math.round(u.total_minutes)} min · ${u.spend?.toFixed(2)}</div>
            </div>
          </button>
          <div className="flex gap-2 mt-3">
            <button data-testid={`admin-grant-${u.user_id}`} onClick={() => grant(u.user_id)}
              className="flex-1 bg-royal/10 text-royal font-semibold text-xs py-2 rounded-lg">+10 sessions</button>
            <button data-testid={`admin-deactivate-${u.user_id}`} onClick={() => deactivate(u.user_id)}
              className="flex-1 bg-rose-50 text-rose-600 font-semibold text-xs py-2 rounded-lg">{u.active ? "Deactivate" : "Inactive"}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CodesTab() {
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState("");
  const [n, setN] = useState(3);
  const load = useCallback(() => api.get("/admin/team-codes").then(({ data }) => setCodes(data.codes)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!code.trim()) return;
    try { await api.post("/admin/team-codes", { code, sessions_granted: Number(n) }); toast.success("Code created"); setCode(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="text-sm font-bold text-navy mb-2">Create a team code</div>
        <div className="flex gap-2">
          <input data-testid="admin-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="EAGLES"
            className="flex-1 px-3 py-2.5 rounded-xl bg-slate-100 outline-none uppercase" />
          <input data-testid="admin-code-sessions" type="number" min={1} value={n} onChange={(e) => setN(e.target.value)}
            className="w-20 px-3 py-2.5 rounded-xl bg-slate-100 outline-none" />
          <button data-testid="admin-code-create" onClick={create} className="bg-royal text-white px-4 rounded-xl"><Plus size={18} /></button>
        </div>
      </div>
      <div className="mt-4 space-y-2" data-testid="admin-codes-list">
        {codes.map((c) => (
          <div key={c.code} className="flex items-center justify-between rounded-xl bg-white border border-slate-200 p-3.5">
            <div className="font-mono font-bold text-navy tracking-wide">{c.code}</div>
            <div className="text-xs text-slate-500">{c.sessions_granted} sessions · used {c.uses}×</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentTab({ onEdit }) {
  const [items, setItems] = useState([]);
  const [onlyPending, setOnlyPending] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/content").then(({ data }) => setItems(data.content)); }, []);
  const pending = items.filter((c) => !c.approved).length;
  const needle = q.trim().toLowerCase();
  const matches = (c) => !needle || [c.title, c.category, c.type, c.athlete?.name, c.athlete?.email, c.content_id].some((v) => (v || "").toLowerCase().includes(needle));
  const shown = items.filter((c) => (needle || !onlyPending || !c.approved) && matches(c));
  return (
    <div className="space-y-2" data-testid="admin-content-list">
      <UploadForm onDone={() => api.get("/admin/content").then(({ data }) => setItems(data.content))} />
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold text-navy">Review queue <span data-testid="admin-pending-count" className="ml-1 bg-amber-100 text-amber-700 text-[11px] px-2 py-0.5 rounded-full">{pending} pending</span></div>
        <button data-testid="admin-toggle-pending" onClick={() => setOnlyPending((v) => !v)} className="text-xs font-semibold text-royal">{onlyPending ? "Show all" : "Pending only"}</button>
      </div>
      <input data-testid="admin-content-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, athlete name or email…"
        className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm mb-2" />
      {shown.length === 0 && <div className="text-sm text-slate-500 text-center py-8" data-testid="admin-queue-empty">Nothing waiting for review.</div>}
      {shown.map((c) => (
        <button key={c.content_id} data-testid={`admin-content-${c.content_id}`} onClick={() => onEdit(c.content_id)}
          className="w-full text-left flex items-center justify-between rounded-xl bg-white border border-slate-200 p-3.5 hover:border-royal transition">
          <div className="min-w-0">
            <div className="font-bold text-navy truncate">{c.title}</div>
            <div className="text-xs text-slate-500 capitalize">{c.type} · {c.category} · {c.lane || ""} {c.is_general ? "· general" : ""}</div>
            {c.athlete && <div className="text-xs text-royal font-semibold mt-0.5">{c.athlete.name} · {c.athlete.position} · {c.athlete.sport} <span className="text-slate-400 font-normal">· {c.athlete.email}</span></div>}
          </div>
          <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${c.approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {c.approved ? "Approved" : "Needs review"}
          </span>
        </button>
      ))}
    </div>
  );
}

function Studio({ contentId, onClose }) {
  const [c, setC] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const isNew = !contentId;

  useEffect(() => {
    if (!contentId) { setC({ title: "", type: "visualization", category: "General", lane: "Defense", guide: "coach_laura", script: "", is_general: true }); return; }
    api.get(`/admin/content/${contentId}`).then(({ data }) => setC(data));
  }, [contentId]);

  if (!c) return <Loader2 className="animate-spin text-royal" />;
  const chars = c.script?.length || 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/content", { content_id: contentId || null, ...c });
      toast.success("Saved");
      if (isNew) onClose();
      else setC((prev) => ({ ...prev, content_id: data.content_id }));
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const approve = async () => {
    const { data } = await api.post(`/admin/content/${contentId}/approve`, { approved: true });
    const when = data.release_at ? new Date(data.release_at) : null;
    toast.success(when && when > new Date() ? `Approved · unlocks ${when.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}` : "Approved & published");
    onClose();
  };
  const rerender = async () => { await api.post(`/admin/content/${contentId}/rerender`); toast.success("Audio will re-render on next play"); };
  const insertAtCursor = (text) => {
    const el = document.querySelector("[data-testid=studio-script]");
    const pos = el?.selectionStart ?? (c.script || "").length;
    const next = (c.script || "").slice(0, pos) + text + (c.script || "").slice(pos);
    setC({ ...c, script: next });
  };
  const doPreview = async () => {
    setLoadingPreview(true); setPreview(null);
    try { const { data } = await api.post("/admin/preview-narration", { text: c.script, guide: c.guide }); setPreview(data.audio_url); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed (check ElevenLabs credits)"); }
    finally { setLoadingPreview(false); }
  };

  return (
    <div data-testid="admin-studio">
      <button onClick={onClose} className="text-royal text-sm font-semibold mb-3">← Back</button>
      <div className="space-y-3">
        <input data-testid="studio-title" value={c.title} onChange={(e) => setC({ ...c, title: e.target.value })} placeholder="Title"
          className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 outline-none font-bold text-navy" />
        <div className="flex flex-wrap gap-2 text-xs">
          {["visualization", "affirmation", "meditation", "frequency"].map((t) => (
            <button key={t} data-testid={`studio-type-${t}`} onClick={() => setC({ ...c, type: t })}
              className={`px-3 py-1.5 rounded-full font-semibold capitalize ${c.type === t ? "bg-royal text-white" : "bg-slate-100 text-slate-600"}`}>{t}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {["Defense", "Offense", "Neutral"].map((l) => (
            <button key={l} data-testid={`studio-lane-${l}`} onClick={() => setC({ ...c, lane: l })}
              className={`px-3 py-1.5 rounded-full font-semibold ${c.lane === l ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
          {["coach_laura", "coach_conner", "calm", "focused", "uplifting"].map((g) => (
            <button key={g} data-testid={`studio-guide-${g}`} onClick={() => setC({ ...c, guide: g })}
              className={`px-3 py-1.5 rounded-full font-semibold capitalize ${c.guide === g ? "bg-turq text-white" : "bg-slate-100 text-slate-600"}`}>{g.replace("_", " ")}</button>
          ))}
        </div>
        <textarea data-testid="studio-script" value={c.script} onChange={(e) => setC({ ...c, script: e.target.value })} rows={12}
          placeholder="Write the script. Use [SFX: bat_crack] cues and ... for pauses."
          className="w-full px-3 py-3 rounded-xl bg-white border border-slate-200 outline-none text-sm font-mono leading-relaxed" />
        <div className={`text-xs font-semibold ${chars > 2400 ? "text-rose-500" : "text-slate-400"}`}>
          {chars} / 2400 chars {chars > 2400 && "· over the cap!"}
        </div>
        <LogicChecker script={c.script} lane={c.lane} sport={c.athlete?.sport || c.sport} position={c.athlete?.position || c.position} onInsert={insertAtCursor} />

        <div className="flex gap-2">
          <button data-testid="studio-preview" onClick={doPreview} disabled={loadingPreview || !c.script}
            className="flex-1 flex items-center justify-center gap-2 bg-slate-100 text-navy font-semibold py-3 rounded-xl disabled:opacity-50">
            {loadingPreview ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Preview narration
          </button>
          <button data-testid="studio-save" onClick={save} disabled={saving}
            className="flex-1 bg-royal text-white font-bold py-3 rounded-xl disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
        {preview && <audio data-testid="studio-audio" src={preview} controls className="w-full mt-1" />}
        {!isNew && (
          <div className="flex gap-2">
            <button data-testid="studio-rerender" onClick={rerender} className="flex-1 bg-slate-100 text-navy font-semibold py-3 rounded-xl text-sm">Re-render audio</button>
            <button data-testid="studio-approve" onClick={approve}
              className="flex-[2] flex items-center justify-center gap-2 bg-emerald-500 text-white font-bold py-3 rounded-xl">
              <Check size={18} /> Approve & publish
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminApp() {
  const [tab, setTab] = useState("dashboard");
  const [editing, setEditing] = useState(undefined); // undefined=list, null=new, id=edit
  const [autoPublish, setAutoPublish] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setAutoPublish(data.auto_publish)); }, []);
  const toggleAuto = async () => { const v = !autoPublish; setAutoPublish(v); await api.post(`/admin/settings/auto-publish?enabled=${v}`); };

  const inStudio = tab === "studio" && editing !== undefined;

  return (
    <div className="mm-frame relative flex flex-col bg-slate-50" data-testid="admin-app">
      <div className="sticky top-0 z-20 bg-navy text-white px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5"><Logo size={30} /><span className="font-bold">Admin</span></div>
        <button data-testid="admin-exit-btn" onClick={() => navigate("/home")} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><HomeIcon size={18} /></button>
      </div>

      <div className="sticky top-[60px] z-10 bg-white border-b border-slate-200 flex overflow-x-auto mm-scroll">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => { setTab(t.id); setEditing(undefined); }}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition ${
              tab === t.id ? "border-royal text-royal" : "border-transparent text-slate-500"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto mm-scroll p-5">
        {tab === "dashboard" && <Dashboard />}
        {tab === "users" && <UsersTab />}
        {tab === "codes" && <CodesTab />}
        {tab === "studio" && (
          inStudio ? <Studio contentId={editing} onClose={() => setEditing(undefined)} />
          : (
            <div>
              <div className="flex items-center justify-between rounded-2xl bg-white border border-slate-200 p-4 mb-4">
                <div>
                  <div className="font-bold text-navy text-sm">Auto-publish</div>
                  <div className="text-xs text-slate-500">Skip approval when the LLM is stable</div>
                </div>
                <button data-testid="admin-autopublish-toggle" onClick={toggleAuto}
                  className={`w-11 h-6 rounded-full transition relative ${autoPublish ? "bg-royal" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${autoPublish ? "left-5" : "left-0.5"}`} />
                </button>
              </div>
              <BackdropPicker />
              <button data-testid="admin-new-script" onClick={() => setEditing(null)}
                className="w-full flex items-center justify-center gap-2 bg-royal text-white font-bold py-3.5 rounded-2xl mb-4">
                <Plus size={18} /> New script
              </button>
              <ContentTab onEdit={(id) => setEditing(id)} />
            </div>
          )
        )}
        {tab === "content" && <ContentTab onEdit={(id) => { setTab("studio"); setEditing(id); }} />}
      </div>
    </div>
  );
}
